// set_app_event, once an App's webhook is on: its URL, and its signing secret revealed only to the
// App's owner from the owner-only route (POST, session-bound, same origin). The secret lives only in
// this component's state while shown; it is never put into the composer, a message, storage, the
// address, a log or the transcript. The tool's output carries the URL and never the secret.
import { useEffect, useId, useState } from 'react';
import { KeyRoundIcon } from 'lucide-react';
import { companyRequest, CompanyRequestError } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appEventsCopy from '@/i18n/ns/app-events';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { WorkspaceIcon } from './icons';

export type AppEventOutcome = {
  id: string;
  title: string;
  eventId: string;
  event: string;
  kind: 'webhook' | 'collection';
  enabled: boolean;
  webhookUrl?: string;
};

const ID = /^[A-Za-z0-9_-]{1,200}$/u;

/** A webhook that is on, with a URL on Chat's own hook path. */
export function isAppWebhookOutcome(value: unknown): value is AppEventOutcome {
  const outcome = value as AppEventOutcome;
  if (!outcome || typeof outcome !== 'object') return false;
  if (outcome.kind !== 'webhook' || outcome.enabled !== true) return false;
  if (typeof outcome.id !== 'string' || !ID.test(outcome.id)) return false;
  if (typeof outcome.eventId !== 'string' || !ID.test(outcome.eventId)) return false;
  if (typeof outcome.title !== 'string' || typeof outcome.event !== 'string') return false;
  try {
    const url = new URL(String(outcome.webhookUrl));
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.pathname.startsWith('/api/v1/hooks/apps/hook_')
      : false;
  } catch {
    return false;
  }
}

type Status = 'hidden' | 'revealing' | 'shown' | 'owner_only' | 'not_enabled' | 'failed';

function useCopied() {
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 1600);
    return () => clearTimeout(timer);
  }, [copied]);
  const write = (key: string, text: string) => {
    // navigator.clipboard is undefined on insecure (http LAN) origins.
    void navigator.clipboard?.writeText(text).then(
      () => setCopied(key),
      () => {},
    );
  };
  return { copied, write };
}

export function AppEventCard({ outcome }: { outcome: AppEventOutcome }) {
  const copy = useNamespaceCopy(appEventsCopy);
  const send = useSessionBoundFetch();
  const headingId = useId();
  const [secret, setSecret] = useState('');
  const [status, setStatus] = useState<Status>('hidden');
  const { copied, write } = useCopied();
  // Nothing outlives the card: the secret is dropped when it unmounts.
  useEffect(() => () => setSecret(''), []);
  const reveal = async () => {
    if (status === 'revealing') return;
    setStatus('revealing');
    try {
      const body = (await companyRequest(
        `/api/v1/company/apps/${encodeURIComponent(outcome.id)}/events/${encodeURIComponent(outcome.eventId)}/secret`,
        'POST',
        {},
        send,
      )) as { secret?: unknown };
      if (typeof body?.secret !== 'string' || !body.secret) throw new Error('secret_missing');
      setSecret(body.secret);
      setStatus('shown');
    } catch (error) {
      const code = error instanceof CompanyRequestError ? error.code : null;
      setStatus(
        code === 'app_event_owner_only'
          ? 'owner_only'
          : code === 'app_webhook_not_enabled'
            ? 'not_enabled'
            : 'failed',
      );
    }
  };
  const hide = () => {
    setSecret('');
    setStatus('hidden');
  };
  const notice =
    status === 'owner_only'
      ? copy.ownerOnly
      : status === 'not_enabled'
        ? copy.notEnabled
        : status === 'failed'
          ? copy.failed
          : null;
  const url = outcome.webhookUrl ?? '';
  return (
    <section
      className="ws-chat-connections ws-app-secret-card ws-app-event-card"
      aria-labelledby={headingId}
      data-testid="app-event-card"
    >
      <div className="ws-chat-connections-heading">
        <strong id={headingId}>{formatCopy(copy.title, { app: outcome.title })}</strong>
      </div>
      <div className="ws-chat-connect-row ws-app-secret-key">
        <span className="ws-chat-connect-logo ws-app-secret-mark" aria-hidden="true">
          <WorkspaceIcon name="globe" size={16} />
        </span>
        <div className="ws-chat-connect-copy">
          <strong>{outcome.event}</strong>
          <small>{copy.urlLabel}</small>
          <code data-testid="app-event-url">{url}</code>
        </div>
        <button
          type="button"
          className="ws-pipedream-connect"
          data-testid="app-event-copy-url"
          onClick={() => write('url', url)}
        >
          {copied === 'url' ? copy.copied : copy.copy}
        </button>
      </div>
      <div className="ws-chat-connect-row ws-app-secret-key">
        <span className="ws-chat-connect-logo ws-app-secret-mark" aria-hidden="true">
          <KeyRoundIcon size={16} strokeWidth={1.75} />
        </span>
        <div className="ws-chat-connect-copy">
          <strong>{copy.secretLabel}</strong>
          {status === 'shown' && <code data-testid="app-event-secret">{secret}</code>}
        </div>
        {status === 'shown' ? (
          <>
            <button
              type="button"
              className="ws-pipedream-connect"
              data-testid="app-event-copy-secret"
              onClick={() => write('secret', secret)}
            >
              {copied === 'secret' ? copy.copied : copy.copy}
            </button>
            <button
              type="button"
              className="ws-pipedream-connect"
              data-testid="app-event-hide-secret"
              onClick={hide}
            >
              {copy.hideSecret}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="ws-pipedream-connect"
            data-emphasis="primary"
            data-state={status === 'revealing' ? 'busy' : 'connect'}
            data-testid="app-event-show-secret"
            disabled={status === 'revealing' || status === 'owner_only'}
            onClick={() => void reveal()}
          >
            {status === 'revealing' ? copy.revealing : copy.showSecret}
          </button>
        )}
      </div>
      <p className="ws-chat-connect-foot">{copy.how}</p>
      {notice && (
        <p
          className="ws-chat-connect-notice ws-app-secret-notice"
          data-tone="problem"
          role="alert"
          data-testid="app-event-notice"
        >
          {notice}
        </p>
      )}
      <p className="ws-chat-connect-foot">{copy.privacy}</p>
    </section>
  );
}
